import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { createInvestorFeedback, getInvestorFeedback, type InvestorFeedbackRecord } from '../../utils/supabase';

const Page = styled.div`
  padding: 24px 20px 100px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Title = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
`;

const Card = styled.div`
  border: 1px solid #333333;
  border-radius: 16px;
  padding: 16px;
  background: #000000;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Label = styled.label`
  font-size: 12px;
  color: #a1a1aa;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TextArea = styled.textarea`
  min-height: 120px;
  resize: vertical;
  background: #000000;
  border: 1px solid #333333;
  border-radius: 12px;
  padding: 12px;
  color: #ffffff;
  font-size: 14px;
`;

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const Count = styled.div<{ $overLimit?: boolean }>`
  font-size: 12px;
  color: ${props => props.$overLimit ? '#ef4444' : '#71717a'};
`;

const Button = styled.button`
  padding: 10px 16px;
  border-radius: 12px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #ffffff;
  font-weight: 700;
  font-size: 13px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Message = styled.div<{ $success?: boolean }>`
  font-size: 13px;
  color: ${props => props.$success ? '#10b981' : '#ef4444'};
`;

const HistoryTitle = styled.h4`
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  margin-top: 4px;
`;

const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const HistoryItem = styled.div`
  border-bottom: 1px solid #222222;
  padding-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const HistoryMeta = styled.div`
  font-size: 11px;
  color: #71717a;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const HistoryText = styled.div`
  font-size: 13px;
  color: #ffffff;
  line-height: 1.5;
`;

const EmptyState = styled.div`
  font-size: 13px;
  color: #71717a;
`;

const InvestorFeedbackPWA: React.FC = () => {
  const [comment, setComment] = useState('');
  const [status, setStatus] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState<InvestorFeedbackRecord[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const investorName = localStorage.getItem('investorName') || '';

  const maxLength = 150;
  const remaining = maxLength - comment.length;
  const overLimit = remaining < 0;

  const loadHistory = async () => {
    if (!investorName) return;
    setLoadingHistory(true);
    const result = await getInvestorFeedback(investorName);
    if (result.success) {
      setHistory(result.feedback || []);
    }
    setLoadingHistory(false);
  };

  useEffect(() => {
    loadHistory();
  }, [investorName]);

  const handleSubmit = async () => {
    if (!investorName) {
      setStatus({ type: 'error', text: 'Missing investor profile.' });
      return;
    }
    if (!comment.trim()) {
      setStatus({ type: 'error', text: 'Please enter feedback before submitting.' });
      return;
    }
    if (overLimit) {
      setStatus({ type: 'error', text: 'Feedback exceeds 150 characters.' });
      return;
    }

    setSubmitting(true);
    const result = await createInvestorFeedback(investorName, comment);
    setSubmitting(false);

    if (!result.success) {
      setStatus({ type: 'error', text: result.error || 'Failed to submit feedback.' });
      return;
    }

    setComment('');
    setStatus({ type: 'success', text: 'Feedback submitted. Thank you!' });
    loadHistory();
  };

  return (
    <Page>
      <Title>Feedback</Title>
      <Card>
        <Label htmlFor="investor-feedback">Message</Label>
        <TextArea
          id="investor-feedback"
          placeholder="Share your feedback or questions..."
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          maxLength={maxLength + 5}
        />
        <FooterRow>
          <Count $overLimit={overLimit}>{remaining} characters remaining</Count>
          <Button onClick={handleSubmit} disabled={submitting || !comment.trim()}>
            {submitting ? 'Submitting...' : 'Send'}
          </Button>
        </FooterRow>
        {status && <Message $success={status.type === 'success'}>{status.text}</Message>}
      </Card>
      <Card>
        <HistoryTitle>Feedback History</HistoryTitle>
        {loadingHistory && <EmptyState>Loading history...</EmptyState>}
        {!loadingHistory && history.length === 0 && <EmptyState>No feedback submitted yet.</EmptyState>}
        {!loadingHistory && history.length > 0 && (
          <HistoryList>
            {history.map((item) => (
              <HistoryItem key={item.id || item.created_at}>
                <HistoryMeta>{item.created_at ? new Date(item.created_at).toLocaleString() : 'Just now'}</HistoryMeta>
                <HistoryText>{item.comment}</HistoryText>
              </HistoryItem>
            ))}
          </HistoryList>
        )}
      </Card>
    </Page>
  );
};

export default InvestorFeedbackPWA;
